import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LogOut, Menu, X, Wallet, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const RoleLayoutShell = ({ title, subtitle, navItems = [] }) => {
  const [open, setOpen] = useState(true);
  const { user, effectiveRoleLabel, logout, hasAnyPermission, permissionSource } = useAuth();
  const navigate = useNavigate();

  const visibleNavItems = navItems.filter((item) => {
    if (item.alwaysVisible) return true;
    if (!item.anyPermissions || item.anyPermissions.length === 0) return true;
    return hasAnyPermission(item.anyPermissions);
  });

  const initials = user?.name
    ? user.name.split(' ').map((item) => item[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen max-h-screen flex" style={{ background: '#f8fafc' }}>
      {open ? (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(30,10,18,0.45)', backdropFilter: 'blur(2px)' }}
          onClick={() => setOpen(false)}
        />
      ) : null}

      <aside
        className="fixed top-0 left-0 h-screen z-50 lg:static lg:z-auto flex flex-col transition-all duration-300"
        style={{
          width: 280,
          background: '#fff',
          boxShadow: '2px 0 18px rgba(15,23,42,0.09)',
          transform: open ? 'translateX(0)' : 'translateX(-280px)',
        }}
      >
        <div className="flex items-center justify-between px-5 py-6" style={{ background: 'linear-gradient(135deg,#c44569,#cf6a87)' }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.35)' }}>
              <Wallet size={20} color="#fff" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-white font-black text-base leading-tight tracking-tighter">{title || 'EMS'}</p>
              <p className="text-white/80 text-xs font-medium">{subtitle || 'Role Workspace'}</p>
            </div>
          </div>
          <button
            type="button"
            className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="mx-4 mt-4 mb-2 rounded-2xl px-4 py-3 flex items-center gap-3" style={{ background: '#fff5f8', border: '1.5px solid #f8d3dd' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shrink-0" style={{ background: 'linear-gradient(135deg,#c44569,#cf6a87)', color: '#fff' }}>
            {initials}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-sm text-slate-800 truncate">{user?.name || 'User'}</p>
            <p className="text-[11px] mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full font-semibold" style={{ color: '#7a1f3d', background: '#fce0e8' }}>
              {effectiveRoleLabel}
            </p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
          <p className="text-xs font-semibold uppercase tracking-widest px-3 py-2" style={{ color: '#c44569', opacity: 0.7 }}>
            Role Modules
          </p>
          {visibleNavItems.map(({ to, label, Icon, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={!!exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                  isActive ? 'text-white shadow-md' : 'text-slate-600 hover:text-slate-800'
                }`
              }
              style={({ isActive }) =>
                isActive ? { background: 'linear-gradient(135deg,#c44569,#cf6a87)', boxShadow: '0 4px 12px rgba(196,69,105,0.25)' } : {}
              }
              onClick={() => {
                if (window.innerWidth < 1024) setOpen(false);
              }}
            >
              <Icon size={16} />
              <span className="flex-1 min-w-0">
                <span className="block truncate">{label}</span>
                {(() => {
                  const navItem = navItems.find((item) => item.to === to);
                  if (!navItem?.anyPermissions?.length) return null;
                  const source = permissionSource(navItem.anyPermissions[0]);
                  if (!source || source === 'System Default Permission') return null;
                  const isOrgHead = source.toLowerCase().includes('head');
                  const isAdmin = source.toLowerCase().includes('admin');
                  return (
                    <span
                      className="inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-bold mt-0.5"
                      style={isOrgHead
                        ? { background: 'rgba(255,220,235,0.9)', color: '#9d174d' }
                        : isAdmin
                          ? { background: 'rgba(219,234,254,0.9)', color: '#1e40af' }
                          : null}
                    >
                      {isOrgHead ? 'by Org Head' : 'by Admin'}
                    </span>
                  );
                })()}
              </span>
              <ChevronRight size={14} />
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-6">
          <div className="border-t mb-4" style={{ borderColor: '#fce0e8' }} />
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all"
            style={{ background: '#fee2e2', color: '#dc2626' }}
          >
            <LogOut size={17} strokeWidth={2.2} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col h-screen">
        <header className="sticky top-0 z-30 flex items-center justify-between px-4 h-14 shrink-0" style={{ background: '#fff', borderBottom: '1.5px solid #fce0e8' }}>
          <button
            onClick={() => setOpen((prev) => !prev)}
            className="flex items-center justify-center w-9 h-9 rounded-xl"
            style={{ background: '#fdf0f4', color: '#c44569' }}
            aria-label="Toggle menu"
          >
            {open ? <X size={19} strokeWidth={2.2} /> : <Menu size={19} strokeWidth={2.2} />}
          </button>
          <div className="text-xs text-slate-500 font-semibold">Organization-scoped workspace</div>
        </header>
        <main className="flex-1 overflow-auto bg-slate-50 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default RoleLayoutShell;
